/**
 * @fileoverview Modern Table component for Breedbase
 *
 * Provides consistent, high-performance table rendering with automatic
 * strategy selection based on data size and configuration.
 *
 * @module Table
 */

import { DEFAULTS, RENDER_MODE, ERROR_CODE } from './constants.js';
import { EventManager } from './EventManager.js';
import { DataFetcher } from './DataFetcher.js';
import { ExportManager } from './ExportManager.js';
import { RendererFactory } from './RendererFactory.js';

/**
 * @typedef {import('./types.js').TableOptions} TableOptions
 * @typedef {import('./types.js').TableMetrics} TableMetrics
 * @typedef {import('./types.js').ColumnDefinition} ColumnDefinition
 */

/**
 * Main Table class
 *
 * @class
 * @example
 * const table = new Table({
 *   container: '#my-table',
 *   ajax: '/api/data',
 *   columns: [
 *     { title: 'Name', data: 'name' },
 *     { title: 'Value', data: 'value' }
 *   ]
 * });
 */
export class Table {
  /**
   * Create a new Table instance
   * @param {TableOptions} options - Configuration options
   */
  constructor(options) {
    this.options = this._mergeOptions(options);
    this.state = this._initializeState();
    this.eventManager = new EventManager();

    this._validateOptions();
    this.init();
  }

  /**
   * Merge user options with defaults
   * @private
   * @param {TableOptions} options - User options
   * @returns {TableOptions} Merged options
   */
  _mergeOptions(options) {
    return {
      ...DEFAULTS,
      ...options,
      // Deep merge for nested objects
      language: { ...DEFAULTS.language, ...options.language },
    };
  }

  /**
   * Initialize component state
   * @private
   * @returns {Object} Initial state
   */
  _initializeState() {
    return {
      container: null,
      renderer: null,
      dataFetcher: null,
      exportManager: null,

      data: null,
      isInitialized: false,
      renderMode: null,

      metrics: {
        loadTime: 0,
        renderTime: 0,
        totalRows: 0,
        initTime: 0
      }
    };
  }

  /**
   * Validate required options
   * @private
   * @throws {Error} If validation fails
   */
  _validateOptions() {
    if (!this.options.container) {
      throw this._createError(
        ERROR_CODE.MISSING_CONTAINER,
        'Table: container is required'
      );
    }
  }

  /**
   * Initialize the table
   * Sets up container, determines render mode, and begins rendering
   */
  async init() {
    if (this.state.isInitialized) {
      console.warn('Table: Already initialized');
      return;
    }

    const initStartTime = performance.now();

    try {
      // Find and validate container
      this._setupContainer();

      // Determine if we need to fetch data
      if (this.options.ajax && !this.options.serverSide) {
        // Client-side AJAX - fetch data first
        await this._fetchData();
      } else if (this.options.data) {
        // Static data provided
        this.state.data = this.options.data;
      }

      // Render the table
      await this._render();

      // Setup export manager if enabled
      if (this.options.enableExport) {
        this._setupExportManager();
      }

      // Bind any handlers registered before initialisation finished
      this._flushPendingEvents();

      this.state.metrics.initTime = performance.now() - initStartTime;
      this.state.isInitialized = true;

      console.log(`Table: Initialized in ${Math.round(this.state.metrics.initTime)}ms`);

      // Call init callback
      if (this.options.onInit) {
        this.options.onInit(this);
      }

    } catch (error) {
      console.error('Table: Initialization failed:', error);

      if (this.options.onError) {
        this.options.onError(error);
      }

      throw error;
    }
  }

  /**
   * Setup container element
   * @private
   */
  _setupContainer() {
    const { container } = this.options;

    // Handle both selector strings and DOM elements
    if (typeof container === 'string') {
      this.state.container = document.querySelector(container);
    } else if (container instanceof HTMLElement) {
      this.state.container = container;
    } else if (typeof jQuery !== 'undefined' && container instanceof jQuery) {
      this.state.container = container[0];
    }

    if (!this.state.container) {
      throw this._createError(
        ERROR_CODE.MISSING_CONTAINER,
        `Table: Container not found: ${container}`
      );
    }
  }

  /**
   * Fetch data using DataFetcher
   * @private
   */
  async _fetchData() {
    const fetchStartTime = performance.now();

    // Create DataFetcher if needed
    if (!this.state.dataFetcher) {
      this.state.dataFetcher = new DataFetcher({
        ajax: this.options.ajax,
        cache: this.options.cache,
        cacheExpiry: this.options.cacheExpiry,
        timeout: this.options.timeout,
        maxRetries: this.options.maxRetries,
        onSuccess: (data, meta) => {
          if (this.options.onLoad) {
            this.options.onLoad({
              ...this.state.metrics,
              fromCache: meta.fromCache
            });
          }
        },
        onError: (error) => {
          if (this.options.onError) {
            this.options.onError(error);
          }
        }
      });
    }

    try {
      this.state.data = await this.state.dataFetcher.fetch();
      this.state.metrics.loadTime = performance.now() - fetchStartTime;
      this.state.metrics.totalRows = this.state.data.length;

    } catch (error) {
      throw this._createError(
        ERROR_CODE.AJAX_ERROR,
        'Failed to fetch table data: ' + error.message,
        { cause: error }
      );
    }
  }

  /**
   * Render the table using appropriate renderer
   * @private
   */
  async _render() {
    const renderStartTime = performance.now();

    const context = {
      options: this.options,
      container: this.state.container,
      data: this.state.data
    };

    try {
      // Create renderer using factory
      this.state.renderer = RendererFactory.createRenderer(context);
      this.state.renderMode = RendererFactory.selectRenderMode(context);

      // Validate render mode requirements
      RendererFactory.validateRenderMode(this.state.renderMode, context);

      // Render
      await this.state.renderer.render();

      this.state.metrics.renderTime = performance.now() - renderStartTime;

      console.log(`Table: Rendered in ${Math.round(this.state.metrics.renderTime)}ms using ${this.state.renderMode}`);

    } catch (error) {
      throw this._createError(
        ERROR_CODE.INIT_ERROR,
        'Failed to render table: ' + error.message,
        { cause: error }
      );
    }
  }

  /**
   * Setup export manager
   * @private
   */
  _setupExportManager() {
    if (!this.state.renderer) {
      return;
    }

    const tableElement = this.state.renderer.tableElement;
    const dataTable = this.state.renderer.getDataTable ? this.state.renderer.getDataTable() : null;

    this.state.exportManager = new ExportManager({
      options: this.options,
      container: this.state.container,
      columns: this.options.columns,
      tableElement: tableElement,
      dataTable: dataTable
    });

    // Add export buttons
    this.state.exportManager.addExportButtons();
  }

  /**
   * Refresh table data
   * Clears cache and reloads
   */
  async refresh() {
    console.log('Table: Refreshing data');

    if (!this.state.isInitialized) {
      console.warn('Table: Cannot refresh - not initialized');
      return;
    }

    try {
      // Clear data fetcher cache
      if (this.state.dataFetcher) {
        this.state.dataFetcher.clearCache();
      }

      // Re-fetch data if using AJAX (client-side)
      if (this.options.ajax && !this.options.serverSide) {
        await this._fetchData();

        // Update renderer with new data
        if (this.state.renderer && typeof this.state.renderer.updateData === 'function') {
          this.state.renderer.updateData(this.state.data);
        } else {
          // Re-render completely
          await this._render();
        }
      } else {
        // Server-side or DOM table - just refresh renderer
        if (this.state.renderer && typeof this.state.renderer.refresh === 'function') {
          this.state.renderer.refresh();
        }
      }

    } catch (error) {
      console.error('Table: Refresh failed:', error);

      if (this.options.onError) {
        this.options.onError(error);
      }
    }
  }

  /**
   * Clear all table data
   */
  clear() {
    if (!this.state.renderer) {
      return;
    }

    if (typeof this.state.renderer.clear === 'function') {
      this.state.renderer.clear();
    }

    this.state.data = null;
    this.state.metrics.totalRows = 0;
  }

  /**
   * Destroy table instance and cleanup
   */
  destroy() {
    console.log('Table: Destroying instance');

    // Cancel any pending requests
    if (this.state.dataFetcher) {
      this.state.dataFetcher.cancel();
    }

    // Destroy renderer
    if (this.state.renderer && typeof this.state.renderer.destroy === 'function') {
      this.state.renderer.destroy();
    }

    // Remove all event listeners
    this.eventManager.offAll();

    // Clear references
    this.state.renderer = null;
    this.state.dataFetcher = null;
    this.state.exportManager = null;
    this.state.data = null;
    this.state.container = null;
    this.state.isInitialized = false;
  }

  /**
   * Get all current table data
   * @returns {Array} Current data
   */
  getData() {
    if (this.state.renderer && typeof this.state.renderer.getData === 'function') {
      return this.state.renderer.getData();
    }
    return this.state.data || [];
  }

  /**
   * Get selected rows (if using DataTables Select plugin)
   * @returns {Array} Selected rows
   */
  getSelectedRows() {
    if (this.state.renderer && typeof this.state.renderer.getSelectedRows === 'function') {
      return this.state.renderer.getSelectedRows();
    }
    return [];
  }

  /**
   * Get performance metrics
   * @returns {TableMetrics} Performance metrics
   */
  getMetrics() {
    const metrics = { ...this.state.metrics };

    // Add data fetcher metrics if available
    if (this.state.dataFetcher) {
      const fetchMetrics = this.state.dataFetcher.getMetrics();
      metrics.dataFetcher = fetchMetrics;
    }

    // Add renderer metrics if available
    if (this.state.renderer && typeof this.state.renderer.getMetrics === 'function') {
      metrics.renderer = this.state.renderer.getMetrics();
    }

    return metrics;
  }

  /**
   * Programmatic search
   * @param {string} term - Search term
   */
  search(term) {
    if (!this.state.renderer || typeof this.state.renderer.search !== 'function') {
      console.warn('Table: Search not available for current renderer');
      return;
    }

    this.state.renderer.search(term);
  }

  /**
   * Programmatic sort
   * @param {number} columnIndex - Column to sort
   * @param {string} direction - Sort direction ('asc' or 'desc')
   */
  sort(columnIndex, direction) {
    if (!this.state.renderer || typeof this.state.renderer.sort !== 'function') {
      console.warn('Table: Sort not available for current renderer');
      return;
    }

    this.state.renderer.sort(columnIndex, direction);
  }

  /**
   * Register a DataTables event handler
   *
   * Callers commonly chain this straight off the constructor
   * (`new CXGN.Table({...}).on('draw', fn)`), but initialisation is
   * asynchronous, so handlers registered that early are queued and bound once
   * the underlying DataTable exists.
   *
   * @param {...*} args - Arguments forwarded to the DataTables `on` method
   * @returns {Table} This instance, for chaining
   */
  on(...args) {
    const dt = this.getDataTable();

    if (dt && typeof dt.on === 'function') {
      dt.on(...args);
    } else {
      this._pendingEvents = this._pendingEvents || [];
      this._pendingEvents.push(args);
    }

    return this;
  }

  /**
   * Remove a DataTables event handler
   * @param {...*} args - Arguments forwarded to the DataTables `off` method
   * @returns {Table} This instance, for chaining
   */
  off(...args) {
    const dt = this.getDataTable();

    if (dt && typeof dt.off === 'function') {
      dt.off(...args);
    }

    return this;
  }

  /**
   * Bind handlers queued by on() before the DataTable existed
   * @private
   */
  _flushPendingEvents() {
    if (!this._pendingEvents || this._pendingEvents.length === 0) {
      return;
    }

    const dt = this.getDataTable();

    if (!dt || typeof dt.on !== 'function') {
      console.warn('Table: event handlers registered but this renderer emits no events');
      this._pendingEvents = [];
      return;
    }

    const pending = this._pendingEvents;
    this._pendingEvents = [];

    pending.forEach(args => dt.on(...args));

    // The first draw already happened while the queue was still filling, so
    // replay it for anything listening on draw.
    const wantsDraw = pending.some(args =>
      typeof args[0] === 'string' && args[0].indexOf('draw') !== -1
    );

    if (wantsDraw) {
      dt.draw(false);
    }
  }

  /**
   * Get underlying renderer instance
   * For advanced operations
   * @returns {Object|null} Renderer instance
   */
  getRenderer() {
    return this.state.renderer;
  }

  /**
   * Get DataTable instance (if using DataTables renderer)
   * @returns {Object|null} DataTable instance
   */
  getDataTable() {
    if (this.state.renderer && typeof this.state.renderer.getDataTable === 'function') {
      return this.state.renderer.getDataTable();
    }
    return null;
  }

  /**
   * Proxy methods to DataTable API (if available)
   * These provide convenient access to DataTables methods
   */

  column(...args) {
    const dt = this.getDataTable();
    return dt ? dt.column(...args) : null;
  }

  columns(...args) {
    const dt = this.getDataTable();
    return dt ? dt.columns(...args) : null;
  }

  row(...args) {
    const dt = this.getDataTable();
    return dt ? dt.row(...args) : null;
  }

  rows(...args) {
    const dt = this.getDataTable();
    return dt ? dt.rows(...args) : null;
  }

  cell(...args) {
    const dt = this.getDataTable();
    return dt ? dt.cell(...args) : null;
  }

  cells(...args) {
    const dt = this.getDataTable();
    return dt ? dt.cells(...args) : null;
  }

  draw(...args) {
    const dt = this.getDataTable();
    return dt ? dt.draw(...args) : null;
  }

  page(...args) {
    const dt = this.getDataTable();
    return dt ? dt.page(...args) : null;
  }

  order(...args) {
    const dt = this.getDataTable();
    return dt ? dt.order(...args) : null;
  }

  /**
   * Create standardized error object
   * @private
   * @param {string} code - Error code
   * @param {string} message - Error message
   * @param {Object} [context] - Additional context
   * @returns {Error} Error object
   */
  _createError(code, message, context = {}) {
    const error = new Error(message);
    error.code = code;
    error.context = context;
    error.timestamp = Date.now();
    return error;
  }
}

export default Table;
